import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  ButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';

// Màu sắc logo
const LOGO_RED_COLOR = '#D40511';
const YELLOW_COLOR = '#FFCC00';

// Xử lý cuộn để thay đổi màu AppBar
const ElevationScroll = (props) => {
  const { children } = props;
  const theme = useTheme();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      backgroundColor: YELLOW_COLOR,
      color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      transition: 'all 0.3s ease-in-out',
      '& .MuiButton-root': {
        color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      },
      '& .MuiIconButton-root': {
        color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      },
      '& .MuiTypography-root': {
        color: LOGO_RED_COLOR,
      },
      '& .MuiTypography-root.logo-text': {
        color: LOGO_RED_COLOR,
      },
    },
  });
};

// StyledMenuItem thu nhỏ padding cố định, nhường chỗ cho thuộc tính 'gap' của Flexbox làm việc
const StyledMenuItem = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  fontSize: '1.1rem', // Chỉnh nhỏ đi 1 xíu xiu so với ban đầu để an toàn hơn
  padding: '8px 8px', // Giảm padding ngang cố định xuống
  position: 'relative',
  transition: 'all 0.3s ease',
  height: '100%',
  whiteSpace: 'nowrap', // Bắt buộc phải có để chữ không tự rớt dòng bên trong nút
  '&:hover': {
    backgroundColor: 'rgba(255, 204, 0, 0.3)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '40%',
      height: '3px',
      backgroundColor: LOGO_RED_COLOR,
      opacity: 0.7,
      borderRadius: '2px',
    },
  },
  '&.active': {
    backgroundColor: 'transparent',
    fontWeight: 600,
    color: '#000000',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60%',
      height: '4px',
      backgroundColor: LOGO_RED_COLOR,
      borderRadius: '2px',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 204, 0, 0.3)',
    },
  },
}));

const Header = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElLang, setAnchorElLang] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleOpenLangMenu = (event) => setAnchorElLang(event.currentTarget);
  const handleCloseLangMenu = () => setAnchorElLang(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    handleCloseLangMenu();
    window.location.reload();
  };

  const navItems = [
    { name: t('header.home'), path: '/' },
    { name: t('header.services'), path: '/services' },
    { name: t('header.industries'), path: '/industries' },
    { name: t('header.blog'), path: '/blog' },
    { name: t('header.support'), path: '/support' },
    { name: t('header.careers'), path: '/careers' },
    { name: t('header.contact'), path: '/contact' },
    { name: t('header.about'), path: '/about' },
  ];

  const languages = [
    { code: 'en', name: 'EN' },
    { code: 'vi', name: 'VN' },
    { code: 'ja', name: 'JP' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', pt: 2 }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '50px', width: 'auto', mixBlendMode: 'multiply' }} />
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to={item.path} 
              sx={{ textAlign: 'center', py: 1.5 }}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight="600">{t('header.changeLanguage') || 'Ngôn ngữ'}</Typography>
        </Box>
        <ButtonGroup fullWidth variant="contained">
          {languages.map((language) => {
            const isActive = i18n.resolvedLanguage === language.code;
            return (
              <Button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                sx={{
                  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                  color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  fontWeight: isActive ? 'bold' : 'normal',
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.grey[300],
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {language.name}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>
    </Box>
  );

  return (
    <div className="pb-20">
      <ElevationScroll>
        <AppBar position="fixed" sx={{ boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {/* Mobile: Hamburger + Logo */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', flexGrow: 1 }}>
                <IconButton size="large" edge="start" onClick={handleDrawerToggle} color="inherit" sx={{ mr: 2 }}>
                  <MenuIcon />
                </IconButton>
                <Box component={RouterLink} to="/" sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', textDecoration: 'none' }}>
                  <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '40px', width: 'auto', mixBlendMode: 'multiply' }} />
                </Box>
              </Box>

              {/* Desktop: Logo */}
              <Box component={RouterLink} to="/" sx={{ mr: 2, display: { xs: 'none', md: 'flex' }, alignItems: 'center', textDecoration: 'none' }}>
                <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '50px', width: 'auto', mixBlendMode: 'multiply' }} />
              </Box>

              {/* CẬP NHẬT CÁCH 3: Desktop Menu với thuộc tính 'gap' tự co giãn */}
              <Box 
                sx={{ 
                  flexGrow: 1, 
                  display: { xs: 'none', md: 'flex' }, 
                  justifyContent: 'center',
                  gap: { md: 0.5, lg: 2, xl: 3 }, // Tự động nén lại trên màn nhỏ (md) và giãn ra trên màn to (lg, xl)
                  overflow: 'hidden' // Ngăn lố layout nếu màn hình ép quá hẹp
                }}
              >
                {navItems.map((item) => {
                  const currentPath = location.pathname;
                  
                  // Logic kiểm tra Active
                  const isActive = (() => {
                    if (item.path === '/' && currentPath === '/') return true;
                    if (item.path === '/' && currentPath !== '/') return false;
                    if (item.path === '/blog') return currentPath.startsWith('/blog');
                    
                    const cleanCurrent = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;
                    const cleanItem = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path;
                    return cleanCurrent === cleanItem;
                  })();

                  return (
                    <StyledMenuItem
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      className={isActive ? 'active' : ''}
                    >
                      {item.name}
                    </StyledMenuItem>
                  );
                })}
              </Box>

              {/* Language Menu - Desktop */}
              <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Tooltip title={t('header.changeLanguage') || 'Change Language'}>
                  <IconButton onClick={handleOpenLangMenu} sx={{ p: 0, mx: 1 }}>
                    <LanguageIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-language"
                  anchorEl={anchorElLang}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorElLang)}
                  onClose={handleCloseLangMenu}
                >
                  {languages.map((language) => (
                    <MenuItem key={language.code} onClick={() => changeLanguage(language.code)}>
                      <Typography textAlign="center">{language.name}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Toolbar>
          </Container>

          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
            }}
          >
            {drawer}
          </Drawer>
        </AppBar>
      </ElevationScroll>
    </div>
  );
};

export default Header;